// ═══════════════════════════════════════════════════════════════════════════
// Rentabilidad — Cascada por orden (función pura compartida FE/Agente)
// ───────────────────────────────────────────────────────────────────────────
// ÚNICA hidratación runtime de rentabilidad por orden del frontend.
//
// Historia (12-jul-2026, fix de fondo de paridad):
//   Hasta hoy esta cascada era una RÉPLICA del pipeline `rows` de
//   ContribucionNetaDashboard.jsx — dos copias de la misma hidratación que
//   ya se desincronizaron dos veces (bug ad.metrics may-2026; versión
//   simplificada sin IVA/IIBB). Ahora la relación se invirtió: el dashboard
//   CONSUME esta lib (hidratarLineasOrden) para armar sus filas, y el
//   clasificador la usa para resolver la renta por orden. Una sola copia →
//   paridad por construcción.
//
//   La fórmula por línea sigue viviendo en src/core/lib/rentabilidad.js
//   (calcularRentabilidadLinea, mirror functions/lib/rentabilidad.js). Esta
//   lib es la capa de NORMALIZACIÓN: de la orden cruda a los inputs por
//   línea (prorrateos, fees enriched, billing, IVA por producto/categoría).
//
// Reglas de borde (alineadas al backend procesarOrden + ADR 042):
//   - tieneCosto = costoUnit > 0. Un producto vinculado con costoPromedio 0
//     está "sin costo cargado" (pendiente_costo), NO calculado con costo $0.
//   - La agregación por orden (calcularRentaNetaOrden) suma SOLO las líneas
//     con costo — igual que la hidratación del dashboard (ADR 042: sin costo,
//     la renta es ficción inflada). Si ninguna línea tiene costo,
//     rentaNeta = null y el caller decide (fallback a persistido o skip).
//
// La función NO hace nada async, no toca Firestore.
// ═══════════════════════════════════════════════════════════════════════════

import { calcularRentabilidadLinea } from '../rentabilidad'
import { resolverIvaPct } from '@modules/productos/hooks/useCategorias'
// La REGLA del reparto del envío vive en UN solo lugar (ADR 078), con su
// mirror backend probado por paridad. Acá sólo se consume.
import { construirEnvioGratisPorItem, envioSellerDeOrden, repartirEnvioSeller } from './repartoEnvio'

// Re-export: los dos consumidores (dashboard + clasificador) importan el
// builder desde esta lib desde el 14-jul. Se mantiene el punto de entrada.
export { construirEnvioGratisPorItem } from './repartoEnvio'

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// ─── Helpers de orden (fuente única — el dashboard importa ESTOS) ──────────

// Subtotal total de la orden (Σ unit_price × qty)
export function ordenSubtotalItems(orden) {
  const items = Array.isArray(orden.order_items) ? orden.order_items : []
  return items.reduce((s, oi) => s + num(oi.unit_price) * num(oi.quantity), 0)
}

// Comisión ML total (preferimos enricher, sino payments)
export function ordenComisionML(orden) {
  const enriched = orden._enriched?.orden?.fees?.marketplaceFee
  if (enriched != null) return num(enriched)
  const pays = Array.isArray(orden.payments) ? orden.payments : []
  const aprobados = pays.filter(p => p.status === 'approved')
  const target = aprobados.length > 0 ? aprobados : pays
  return target.reduce((s, p) => s + num(p.marketplace_fee), 0)
}

// sale_fee de un ítem (cuando el enricher la trajo). ⚠️ Es POR UNIDAD (así lo
// manda ML en `order_items[].sale_fee`): la comisión de la línea es × cantidad.
// Medido el 6-oct-2026 en 3.325 ventas de Feel Baza: las líneas de una unidad
// dan 23,0 % del precio; las de varias, 23,8 % por unidad y 8,0 % si se la toma
// como de la línea entera — que es lo que se hacía y subestimaba la comisión.
export function ordenSaleFeePorItem(orden, itemId) {
  return num(orden._enriched?.orden?.saleFeesPorItem?.[itemId])
}

// ─── Builders de índices (fuente única — clasificador Y dashboard) ─────────

/**
 * itemId → { productoId, costoUnit, producto, categoria } vía vínculos del
 * catálogo. costoUnit es null cuando el producto no tiene costoPromedio > 0
 * (regla backend: costoUnit > 0 o pendiente_costo).
 */
export function construirProductoInfoPorItem({ productos = [], catalogoLinks = [], categorias = [] } = {}) {
  const prodById = new Map()
  for (const p of productos) prodById.set(p.id, p)
  const catById = new Map()
  for (const c of categorias) catById.set(c.id, c)
  const map = new Map()
  for (const link of catalogoLinks) {
    if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
    const prod = prodById.get(link.productoId)
    if (!prod) continue
    const categoria = prod.categoriaId ? catById.get(prod.categoriaId) : null
    const costoRaw = Number(prod.costoPromedio)
    map.set(link.externalItemId, {
      productoId: prod.id,
      costoUnit: Number.isFinite(costoRaw) && costoRaw > 0 ? costoRaw : null,
      producto: prod,
      categoria: categoria || null,
    })
  }
  return map
}

/**
 * itemId → costo de publicidad por unidad (cost / units_quantity del ad).
 * Si hay varios ads para el mismo item, gana el más caro por unidad
 * (conservador). Soporta métricas anidadas (`ad.metrics`) y planas.
 */
export function construirCostoAdPorItem(adsItems = []) {
  const map = new Map()
  for (const ad of adsItems) {
    const itemId = ad.item_id || (ad.id ? String(ad.id).split('_').pop() : null)
    if (!itemId) continue
    const m = ad.metrics || ad
    const cost = num(m.cost)
    const units = num(m.units_quantity)
    if (cost <= 0 || units <= 0) continue
    const perUnit = cost / units
    const prev = map.get(String(itemId))
    if (prev == null || perUnit > prev) map.set(String(itemId), perUnit)
  }
  return map
}

// ─── Hidratación por línea (el corazón compartido) ──────────────────────────

/**
 * Hidrata TODAS las líneas de una orden con la cascada financiera completa.
 * Es la única normalización orden → inputs de calcularRentabilidadLinea del
 * frontend: el `rows` del dashboard arma sus filas desde acá, y
 * calcularRentaNetaOrden agrega desde acá.
 *
 * NO saltea líneas (ni subtotal ≤ 0): el dashboard las muestra todas.
 * La agregación decide qué suma (ver calcularRentaNetaOrden).
 *
 * @returns {Object} {
 *   subtotalOrden, comisionTotal, envioTotal, tieneBilling,
 *   lineas: Array<{
 *     oi,                      // referencia al order_item crudo (UI: título, thumbnail, csv)
 *     itemId, qty, unit, subtotal, prorrateo,
 *     precioLista, fullUnitEfectivo, descuento, descuentoPct, costoPromo,
 *     comisionMLFinal, comisionPura, fixedFee, cargoCuotas,
 *     fixedFeeFuente, cargoCuotasFuente,
 *     envioSeller, origenes,   // origenes.envio: el origen del envío de la orden
 *     costoUnit, costoProducto, tieneLink, tieneCosto,
 *     adUnit, tieneAd, publicidad,
 *     ivaPct, ivaSource,
 *     rentResult,              // output completo de calcularRentabilidadLinea
 *   }>
 * }
 */
export function hidratarLineasOrden({
  venta,
  productoInfoPorItem,
  costoAdPorItem,
  impuestosEmpresa,
  envioGratisPorItem,   // opcional (reporte ymCwUxBd, opción A) — Map itemId→bool
}) {
  const orden = venta || {}
  const items = Array.isArray(orden.order_items) ? orden.order_items : []
  const subtotalOrden = ordenSubtotalItems(orden) || num(orden.total_amount)
  const comisionTotal = ordenComisionML(orden)
  // El envío de la orden y su origen salen del par repartoEnvio (con su
  // espejo backend probado por paridad): el origen viaja a cada línea.
  const { monto: envioTotal, origen: origenEnvio } = envioSellerDeOrden(orden)

  // ─── Envío seller: la REGLA vive en repartoEnvio.js (ADR 078), y su mirror
  //     backend corre la misma tabla de casos. Acá sólo se consume: el
  //     resultado es paralelo a `items`. ─────────────────────────────────────
  const envioPorLinea = repartirEnvioSeller({
    items, envioTotal, subtotalOrden, envioGratisPorItem,
  }).porLinea

  const impuestos = impuestosEmpresa || {}
  const flags = impuestos.flags || {
    discriminaIVA: true,
    pagaIIBB: true,
    pagaImpCredDebito: true,
    aplicaGananciasEstimado: true,
  }

  // Billing real del enricher (desglose selling/fixed/financing) — se
  // prorratea por línea. Sin billing: un solo TOTAL, sin descomponer.
  const billing = orden._enriched?.billingOrden || null
  const tieneBilling = !!billing

  const lineas = []

  for (let idx = 0; idx < items.length; idx++) {
    const oi = items[idx]
    const itemId = oi?.item?.id ? String(oi.item.id) : null
    const qty = num(oi.quantity)
    const unit = num(oi.unit_price)
    const subtotal = unit * qty
    const prorrateo = subtotalOrden > 0 ? subtotal / subtotalOrden : 0

    // ─── Descuento / precio lista (3 fuentes ML, gana el mayor) ───
    const fullUnit = num(oi.full_unit_price)
    const grossPrice = num(oi.gross_price)
    const descuentosArr = Array.isArray(oi.discounts) ? oi.discounts : []
    const descuentoFromDiscountsTotal = descuentosArr.reduce(
      (s, d) => s + num(d?.amounts?.full),
      0,
    )
    const candidatos = []
    if (fullUnit > unit) candidatos.push(fullUnit * qty)
    if (grossPrice > subtotal) candidatos.push(grossPrice)
    if (descuentoFromDiscountsTotal > 0) candidatos.push(subtotal + descuentoFromDiscountsTotal)
    const precioLista = candidatos.length > 0 ? Math.max(...candidatos) : subtotal
    const fullUnitEfectivo = qty > 0 ? precioLista / qty : unit
    const descuento = Math.max(0, precioLista - subtotal)
    const descuentoPct = precioLista > 0 ? descuento / precioLista : 0

    // ─── Comisión: sale_fee del enricher (por unidad × cantidad) > prorrateo del total ───
    const saleFeeLinea = itemId ? ordenSaleFeePorItem(orden, itemId) * qty : 0
    const comisionMLTotal = saleFeeLinea > 0 ? saleFeeLinea : comisionTotal * prorrateo

    const envioSeller = envioPorLinea[idx]

    // ─── Producto del catálogo (costo + IVA por producto/categoría) ───
    const prodInfo = itemId ? (productoInfoPorItem?.get(itemId) || null) : null
    const tieneLink = !!prodInfo
    const costoUnit = num(prodInfo?.costoUnit)
    const tieneCosto = costoUnit > 0
    const costoProducto = costoUnit * qty

    // ─── Publicidad atribuida por unidad ───
    const adUnit = itemId ? num(costoAdPorItem?.get(itemId)) : 0
    const tieneAd = !!(itemId && costoAdPorItem?.has(itemId))
    const publicidad = adUnit * qty

    // ─── Desglose de comisión (billing real vs total agregado) ───
    let comisionPura, fixedFee, cargoCuotas, fixedFeeFuente, cargoCuotasFuente
    if (tieneBilling) {
      comisionPura = num(billing.sellingFee) * prorrateo
      fixedFee = num(billing.fixedFee) * prorrateo
      cargoCuotas = num(billing.financingCharge) * prorrateo
      fixedFeeFuente = 'billing'
      cargoCuotasFuente = 'billing'
    } else {
      comisionPura = comisionMLTotal
      fixedFee = 0
      cargoCuotas = 0
      fixedFeeFuente = 'sin-billing'
      cargoCuotasFuente = 'sin-billing'
    }

    const billingParaCalculo = tieneBilling
      ? { sellingFee: comisionPura, fixedFee, financingCharge: cargoCuotas }
      : null

    // ─── IVA: cascada producto → categoría → empresa → plataforma ───
    const ivaResuelto = resolverIvaPct({
      producto: prodInfo?.producto || null,
      categoria: prodInfo?.categoria || null,
      empresaImpuestos: impuestos,
      defaultPlataforma: 0.21,
    })
    const ivaPct = ivaResuelto.ivaPct ?? (num(impuestos.ivaGeneral) || 0.21)
    const ivaSource = ivaResuelto.source

    // ─── Fórmula pura compartida FE/BE ───
    const rentResult = calcularRentabilidadLinea({
      tieneLink,
      tieneCosto,
      subtotal,
      qty,
      costoUnit,
      comisionMLTotal,
      envioSellerLinea: envioSeller,
      billing: billingParaCalculo,
      adUnit,
      ivaPct,
      flags,
      iibbRate: impuestos.iibb,
      impCredDebitoRate: impuestos.impuestoCredDebito,
      factorGanancias: impuestos.factorGanancias,
    })

    lineas.push({
      oi,
      itemId,
      qty,
      unit,
      subtotal,
      prorrateo,
      precioLista,
      fullUnitEfectivo,
      descuento,
      descuentoPct,
      costoPromo: descuento,
      comisionMLFinal: comisionMLTotal,
      comisionPura,
      fixedFee,
      cargoCuotas,
      fixedFeeFuente,
      cargoCuotasFuente,
      envioSeller,
      // Misma clave `envio` que la línea persistida (`porLinea.origenes`): las
      // pantallas derivan el «no informado» con derivarCostosNoInformados.
      origenes: { envio: origenEnvio },
      costoUnit,
      costoProducto,
      tieneLink,
      tieneCosto,
      adUnit,
      tieneAd,
      publicidad,
      ivaPct,
      ivaSource,
      rentResult,
    })
  }

  return { subtotalOrden, comisionTotal, envioTotal, tieneBilling, lineas }
}

// ─── Agregación por orden ────────────────────────────────────────────────────

/**
 * Rentabilidad neta de una orden COMPLETA — construida sobre
 * hidratarLineasOrden, agregando SOLO las líneas con costo (ADR 042, el
 * mismo criterio con que el dashboard hidrata `_rentabilidad` runtime).
 *
 * @returns {Object} {
 *   rentaNeta:   number|null   // null si NINGUNA línea tiene costo cargado
 *   publicidad:  number        // publicidad de las líneas con costo
 *   subtotal:    number        // subtotal de la orden (ingreso)
 *   lineasConCosto: number
 *   _fuente:     'cascada-completa'
 *   lineas:      las líneas de hidratarLineasOrden (todas)
 * }
 */
export function calcularRentaNetaOrden({
  venta,
  productoInfoPorItem,
  costoAdPorItem,
  impuestosEmpresa,
  envioGratisPorItem,
}) {
  const hid = hidratarLineasOrden({ venta, productoInfoPorItem, costoAdPorItem, impuestosEmpresa, envioGratisPorItem })

  let rentaNeta = null
  let publicidad = 0
  let lineasConCosto = 0
  for (const l of hid.lineas) {
    if (!l.tieneCosto) continue
    lineasConCosto++
    rentaNeta = (rentaNeta ?? 0) + num(l.rentResult.rentaNeta)
    publicidad += l.publicidad
  }

  return {
    rentaNeta,
    publicidad,
    subtotal: hid.subtotalOrden,
    lineasConCosto,
    _fuente: 'cascada-completa',
    lineas: hid.lineas,
  }
}
